/**
 * Pure display formatters for the exact `TypedColumn` substrate - extracted
 * from `app/src/lib/parquet-result-set-source.ts` into `@ic/report-row-model`
 * (FE-13) so `columnar-aggregation.ts`'s `formatAggregateResult()` can format
 * a decimal/date/timestamp aggregate WITHOUT importing that file, which pulls
 * in `parquet-decode.ts` -> `parquet-wasm/esm` (a browser-bundler-shaped,
 * WASM-backed dependency the Lambda export path has no reason to carry - see
 * `parquet-decode.ts`'s own header on why its wasm init is written the way it
 * is). None of these three functions ever touch Parquet or the DOM; they are
 * plain `bigint`/`Date` math, unchanged from their original bodies.
 *
 * `app/src/lib/parquet-result-set-source.ts` re-exports all three unchanged,
 * so every existing frontend import of `formatDate`/`formatDecimal`/
 * `formatTimestamp` from that module keeps working.
 */

/** Groups an unsigned decimal digit string's integer part with commas,
 *  operating on the digit string itself - never a float - so grouping a
 *  15+ digit money value never risks the precision loss converting through
 *  `Number` would (§7 Rule 1: display is a formatted string, not a number).
 *  A plain loop, not a lookahead-based regex (`/\B(?=(\d{3})+(?!\d))/g`) -
 *  that pattern's nested quantifier inside a lookahead has super-linear
 *  worst-case runtime (catastrophic backtracking), a real risk given this
 *  runs on every rendered money cell. This is O(n) with no backtracking. */
function groupThousands(digits: string): string {
  const groups: string[] = [];
  let end = digits.length;
  while (end > 3) {
    groups.unshift(digits.slice(end - 3, end));
    end -= 3;
  }
  groups.unshift(digits.slice(0, end));
  return groups.join(',');
}

/** Renders an exact unscaled integer + scale as `"1,234.5600"` /
 *  `"-9,876.5400"` - the same shape `RESULTS.md`'s benchmark verified
 *  round-trips exactly against the SQL-side decimal sum. */
export function formatDecimal(unscaled: bigint, scale: number): string {
  const negative = unscaled < 0n;
  const abs = negative ? -unscaled : unscaled;
  const digits = abs.toString().padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale) || '0';
  const frac = digits.slice(digits.length - scale);
  const groupedWhole = groupThousands(whole);
  return (negative ? '-' : '') + groupedWhole + (scale > 0 ? '.' + frac : '');
}

/**
 * The same exact rendering as {@link formatDecimal}, but WITHOUT thousands
 * grouping — `"1234.5600"`, not `"1,234.5600"`. This is the wire shape a
 * currency `ExportInlineColumn`'s row value must carry (IC-601, FE-13's
 * export-request builder): `resolved-view-export.ts`'s `parseUnscaledDecimal`
 * reconstructs the unscaled bigint by stripping only the decimal point, so a
 * comma in the string would corrupt the parse (`BigInt("1,234")` throws).
 * `formatDecimal` is for on-screen display only; use this for any value
 * headed back over the wire.
 */
export function formatDecimalRaw(unscaled: bigint, scale: number): string {
  const negative = unscaled < 0n;
  const abs = negative ? -unscaled : unscaled;
  const digits = abs.toString().padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale) || '0';
  const frac = digits.slice(digits.length - scale);
  return (negative ? '-' : '') + whole + (scale > 0 ? '.' + frac : '');
}

/** Renders a naive (zone-less) microsecond timestamp for display, without
 *  ever passing through a real timezone conversion (§7 Rule 2 - the stored
 *  value is a wall-clock reading, not a UTC instant, so `toLocaleString()`
 *  or any local-offset application would silently shift it). `Date`'s own
 *  UTC rendering is used purely as a calendar/clock digit formatter, then
 *  the sub-millisecond digits `Date` can't hold are appended from the exact
 *  `bigint` micros value - so display keeps full precision too, even though
 *  only the compare/aggregate representation is REQUIRED to (§7 Rule 1). */
export function formatTimestamp(micros: bigint): string {
  const wholeMs = micros / 1000n;
  const subMs = ((micros % 1000n) + 1000n) % 1000n; // last 3 digits, sign-safe
  const iso = new Date(Number(wholeMs)).toISOString(); // "...T...:ss.mmmZ"
  // `toISOString()` always emits exactly one '.' (the milliseconds
  // separator), so `base` is never undefined — pre-existing code, newly
  // flagged only because IC-601's `formatDecimalRaw` addition above put this
  // file in a quality-gate scan for the first time.
  const base = iso.split('.')[0]!;
  const ms = iso.slice(base.length + 1, base.length + 4);
  return `${base}.${ms}${subMs.toString().padStart(3, '0')}`;
}

/** Renders a zone-aware timestamp (a real UTC instant - SQL `datetimeoffset`) as
 *  the VIEWER'S local wall-clock reading, in the same zone-less shape as
 *  {@link formatTimestamp} so every consumer that parses it (`new Date(s)` reads a
 *  zone-less ISO string as local time) lands on the right instant. The zone comes
 *  from the runtime (`Date`'s local fields), never a hard-coded one. */
export function formatZonedTimestamp(micros: bigint): string {
  const subMs = ((micros % 1000n) + 1000n) % 1000n;
  const d = new Date(Number(micros / 1000n));
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return (
    `${p(d.getFullYear(), 4)}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
    `T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}` +
    subMs.toString().padStart(3, '0')
  );
}

/** Picks the right renderer for a timestamp column: local time when `zoned`, the stored wall-clock otherwise. */
export function formatColumnTimestamp(micros: bigint, zoned?: boolean): string {
  return zoned ? formatZonedTimestamp(micros) : formatTimestamp(micros);
}

export function formatDate(epochDays: number): string {
  return new Date(epochDays * 86_400_000).toISOString().slice(0, 10);
}

/** Matches exactly what `formatDecimal()` emits: an optional sign, a
 *  comma-grouped or ungrouped integer part, and an optional fractional part.
 *  Anchored and backtracking-free. */
const DECIMAL_TEXT = /^-?(?:\d{1,3}(?:,\d{3})*|\d+)(?:\.\d+)?$/;

/** The exact inverse of {@link formatDecimal} - recovers the unscaled integer
 *  and scale from a rendered decimal string using string math only, never
 *  `Number`/`parseFloat` (IC-499: coercing `"1,000.0000"` through `Number()`
 *  yields `NaN` because of the comma, and coercing an ungrouped one yields a
 *  float, reintroducing exactly the precision loss the decimal substrate
 *  exists to prevent - see `parquet-result-set-source.ts`'s own note).
 *
 *  Returns `null` for anything that is not a decimal rendering, so callers can
 *  degrade to a placeholder rather than emitting `NaN`. */
export function parseDecimal(text: string | number): { unscaled: bigint; scale: number } | null {
  // A `number` is accepted and converted via its own shortest round-trip
  // rendering. That is not the coercion this function exists to prevent -
  // the hazard is pushing an exact decimal *string* through `Number()`, not
  // reading a value that was already a number (an inline result set, a
  // hand-built fixture). Non-finite numbers have no decimal rendering.
  if (typeof text === 'number') {
    if (!Number.isFinite(text)) return null;
    return parseDecimal(text.toString());
  }
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!DECIMAL_TEXT.test(trimmed)) return null;
  const negative = trimmed.startsWith('-');
  const digits = (negative ? trimmed.slice(1) : trimmed).replaceAll(',', '');
  const dot = digits.indexOf('.');
  const whole = dot === -1 ? digits : digits.slice(0, dot);
  const frac = dot === -1 ? '' : digits.slice(dot + 1);
  const unscaled = BigInt(whole + frac);
  return { unscaled: negative ? -unscaled : unscaled, scale: frac.length };
}

/** Sums rendered decimal strings **exactly** - parses each back to its
 *  unscaled `bigint`, aligns to the widest scale, adds, and re-renders. Never
 *  converts through a float, so a batch total matches the SQL-side sum digit
 *  for digit (the property `RESULTS.md`'s benchmark pins).
 *
 *  `null`/`undefined` entries are skipped. Returns `null` if any entry is a
 *  non-empty value that does not parse - a partially-summed total is worse
 *  than an honest placeholder when the number is money. */
export function sumDecimal(values: readonly (string | number | null | undefined)[]): string | null {
  let total = 0n;
  let scale = 0;
  const parsed: { unscaled: bigint; scale: number }[] = [];
  for (const value of values) {
    if (value == null || value === '') continue;
    const item = parseDecimal(value);
    if (item === null) return null;
    parsed.push(item);
    if (item.scale > scale) scale = item.scale;
  }
  for (const item of parsed) {
    total += item.unscaled * 10n ** BigInt(scale - item.scale);
  }
  return formatDecimal(total, scale);
}

/** Re-renders an exact decimal at a fixed fraction-digit count, rounding
 *  half-away-from-zero on the `bigint` itself. Used for money display, where
 *  the substrate carries scale 4 (SQL `money`) but the UI shows 2. */
export function rescaleDecimal(unscaled: bigint, scale: number, digits: number): string {
  if (scale === digits) return formatDecimal(unscaled, digits);
  if (scale < digits) return formatDecimal(unscaled * 10n ** BigInt(digits - scale), digits);
  const divisor = 10n ** BigInt(scale - digits);
  const negative = unscaled < 0n;
  const abs = negative ? -unscaled : unscaled;
  const quotient = abs / divisor;
  const rounded = abs % divisor * 2n >= divisor ? quotient + 1n : quotient;
  return formatDecimal(negative ? -rounded : rounded, digits);
}
